import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/shared/ui/Button';

import { useRequestExecution } from '../api/rules';

/* Run one rule, from wherever that rule is listed.

 * **One component for both surfaces, and that is the point rather than tidiness.** The Rules screen
 * and a dataset's own Rules tab both want this, and two implementations would be two answers to the
 * questions that actually matter here: what the button runs, whether it says so, and where the reader
 * lands afterwards. On a control that opens connections to a customer's production database, those
 * are not details to settle twice.
 *
 * **It runs one binding, and the label is therefore honest.** `PlanRequest.binding_ids` scopes the
 * plan to a single binding â€” "omit to plan every enabled binding" is the contract's own wording for
 * the alternative â€” so `Run` beside a rule runs *that* rule. Before this the only mutation available
 * sent `dataset_ids`, and a button here would have run every rule bound to the table while saying it
 * ran one: a control that does more than its label, on somebody else's database.
 *
 * **`datasetId` narrows it further and is optional.** From a dataset's Rules tab the reader means
 * "this rule, on this table", and a binding written as a name glob may resolve to hundreds â€” so
 * without it the button on one dataset's page would scan the whole estate. From the Rules screen
 * there is no such context and the binding's full resolved set is exactly what is meant.
 *
 * **It navigates to the run rather than reporting inline.** A 202 gives back an execution whose tasks
 * have not started; the useful next screen is the one that shows them arriving, and it is now a real
 * page. Reporting "started" in place would leave the reader on a list that will not change for
 * several seconds, with no way to the thing they just made.
 *
 * **A disabled binding cannot be run**, and refusing here rather than at the source is the honest
 * order: the plan would resolve to nothing and the route answers 409 for a plan covering nothing, so
 * the failure would arrive as an error about coverage rather than about the binding being unbound.
 */
export function RunBindingButton({
  bindingId,
  enabled,
  datasetId,
  onQueued,
  appearance,
}: {
  readonly bindingId: string;
  /** A disabled binding renders no control. See the note above. */
  readonly enabled: boolean;
  /** Narrows the run to one dataset. Omit to run everything the binding resolves to. */
  readonly datasetId?: string | undefined;
  /** What to do with the run once the 202 comes back.
   *
   * **Given, the caller reports the run in place; omitted, this navigates to it.** The two are for
   * two different surroundings. On a dataset's Rules tab there is nothing to report into, so going
   * to the run is the only way to see it. On the bindings ledger a reader is working *down a list* â€”
   * they may want to run three rules â€” and being thrown onto a run's page after the first one is
   * losing their place to show them something they can reach from a link. So that screen takes the
   * run and says what it queued, with a way through. */
  readonly onQueued?: ((execution: { readonly id: string }) => void) | undefined;
  /** A link-styled control rather than a bordered button, for a dense ledger row. */
  readonly appearance?: 'link';
}): ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const execute = useRequestExecution();

  if (!enabled) return null;

  const request = (): void => {
    execute.mutate(
      {
        bindingIds: [bindingId],
        ...(datasetId === undefined ? {} : { datasetIds: [datasetId] }),
      },
      {
        /* `onSuccess` rather than after `mutate`: the execution id only exists once the 202 has come
           back, and acting before it would need an id nobody has. A failure stays on this screen
           with the error, which is where the reader can try again. */
        onSuccess: (execution) => {
          if (onQueued !== undefined) {
            onQueued(execution);
            return;
          }
          navigate(`/executions/${execution.id}`);
        },
      },
    );
  };

  const meaning =
    datasetId === undefined ? t('rules.run.meaningAll') : t('rules.run.meaningDataset');

  const control =
    appearance === 'link' ? (
      <button
        type="button"
        className="lq-rowaction"
        disabled={execute.isPending}
        title={meaning}
        onClick={request}
      >
        {execute.isPending ? t('rules.run.queueing') : t('rules.run.now')}
      </button>
    ) : (
      /* The title says what the button cannot: that this reaches the customer's source. The label
         stays one word because it sits in a table cell beside three others. */
      <Button
        tone="secondary"
        size="compact"
        busy={execute.isPending}
        title={meaning}
        onClick={request}
      >
        {t('rules.run.label')}
      </Button>
    );

  /* **A refused run says why, beside the button that was pressed.** This rendered nothing on
     failure for as long as it existed, while both comments above claimed "a failure stays on this
     screen with the error" â€” so a 409 ("an execution of this plan is already in progress"), a
     covers-nothing refusal or a quota refusal all read as a button that did nothing. The server's
     own sentence is shown, because each refusal has a different fix and only the server knows which
     one applied. A new press clears it: `mutate` resets the error as it starts. */
  if (!execute.isError) return control;
  return (
    <span className="lq-runbind">
      {control}
      <span className="lq-runbind__error" role="alert">
        {t('rules.run.refused', { reason: execute.error.message })}
      </span>
    </span>
  );
}
